import { Link } from "@tanstack/react-router"
import { EntireLogo } from "@ui/components/EntireLogo"
import { useCookieConsent } from "@ui/lib/useCookieConsent"
import { usePostHog } from "posthog-js/react"
import { useSyncExternalStore } from "react"
import type { ReactNode } from "react"

import { companyLinks, connectLinks, legalLinks, productLinks } from "@/components/nav-data"
import { SystemStatus } from "@/components/SystemStatus"

import { SiteSection } from "./SiteSection"

const footerLinkClassName = "text-site-footer font-sans text-muted hover:text-default"

function subscribeToHydration() {
  return () => undefined
}

function getClientHydrationSnapshot() {
  return true
}

function getServerHydrationSnapshot() {
  return false
}

function FooterLink({
  href,
  label,
  onClick,
}: {
  href: string
  label: string
  onClick?: () => void
}) {
  if (href.startsWith("http")) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={footerLinkClassName}
        onClick={onClick}
      >
        {label}
      </a>
    )
  }

  return (
    <Link to={href} className={footerLinkClassName}>
      {label}
    </Link>
  )
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-site-footer font-sans text-default">{title}</h3>
      <ul className="mt-6 space-y-3">{children}</ul>
    </div>
  )
}

export function SiteFooter() {
  const { requiresConsent, openBanner } = useCookieConsent()
  const posthog = usePostHog()
  const isMounted = useSyncExternalStore(
    subscribeToHydration,
    getClientHydrationSnapshot,
    getServerHydrationSnapshot,
  )

  return (
    <SiteSection innerClassName="flex min-h-[380px] flex-col gap-6 px-8 py-8">
      <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
        <FooterColumn title="Product">
          {productLinks.map((link) => (
            <li key={link.href}>
              <FooterLink
                href={link.href}
                label={link.label}
                onClick={
                  link.label === "Documentation"
                    ? () => posthog.capture("docs_link_clicked")
                    : undefined
                }
              />
            </li>
          ))}
        </FooterColumn>
        <FooterColumn title="Company">
          {companyLinks.map((link) => (
            <li key={link.href}>
              <FooterLink href={link.href} label={link.label} />
            </li>
          ))}
        </FooterColumn>
        <FooterColumn title="Legal">
          {legalLinks.map((link, index) => (
            <li key={link.href}>
              {index === 1 && isMounted && requiresConsent && (
                <button
                  className="text-site-footer mb-3 block font-sans text-muted hover:text-default"
                  onClick={openBanner}
                >
                  Cookie Settings
                </button>
              )}
              <FooterLink href={link.href} label={link.label} />
            </li>
          ))}
        </FooterColumn>
        <FooterColumn title="Connect">
          {connectLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={footerLinkClassName}
                onClick={
                  link.label === "Discord" ? () => posthog.capture("discord_clicked") : undefined
                }
              >
                {link.label}
              </a>
            </li>
          ))}
        </FooterColumn>
        <div className="hidden justify-self-end text-neutral-200 lg:block dark:text-neutral-800">
          <EntireLogo className="h-8 w-auto" />
        </div>
      </div>
      <div className="text-site-footer flex flex-col gap-6 font-sans text-muted md:flex-row md:items-center md:justify-end">
        <div className="flex items-center gap-6">
          <SystemStatus
            className=""
            pulse
            textColorClassName="text-default hover:text-default"
            textSize="font-sans text-site-footer"
          />
          <span className="text-site-footer font-sans text-muted">© 2026 Entire Inc.</span>
        </div>
      </div>
    </SiteSection>
  )
}
